/**
 * Gateway event state hook.
 *
 * Tracks gateway-level events that aren't part of the chat stream:
 * - shutdown: gateway is restarting
 * - presence: connected devices/clients
 * - update.available: new gateway version
 * - exec.approval.requested/resolved: command approval workflow
 *
 * Each event type produces state that UI components can consume
 * (banners, badges, notifications).
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  ShutdownPayload,
  PresencePayload,
  PresenceEntry,
  UpdateAvailablePayload,
  ExecApprovalRequestedPayload,
  ExecApprovalResolvedPayload,
} from '../types/protocol';

// ── Types ──

export interface ShutdownState {
  /** Whether a shutdown is in progress */
  active: boolean;
  /** Reason for shutdown */
  reason: string;
  /** Expected time until restart (ms from event receipt), or null if unknown */
  restartExpectedMs: number | null;
  /** Timestamp when shutdown event was received */
  receivedAt: number;
}

export interface ExecApprovalRequest {
  /** Unique approval ID */
  id: string;
  /** Command or description needing approval */
  command: string;
  /** When the request was created */
  createdAtMs: number;
  /** When the request expires */
  expiresAtMs: number;
}

export interface GatewayEventsState {
  /** Active shutdown state (null if no shutdown in progress) */
  shutdown: ShutdownState | null;
  /** Connected presence entries */
  presence: PresenceEntry[];
  /** Whether a gateway update is available */
  gatewayUpdateAvailable: {
    currentVersion: string;
    latestVersion: string;
    channel?: string;
  } | null;
  /** Pending exec approval requests */
  pendingApprovals: ExecApprovalRequest[];
}

export interface UseGatewayEventsReturn extends GatewayEventsState {
  /** Handle a shutdown event */
  handleShutdown: (payload: ShutdownPayload) => void;
  /** Handle a presence event */
  handlePresence: (payload: PresencePayload) => void;
  /** Handle an update.available event */
  handleUpdateAvailable: (payload: UpdateAvailablePayload) => void;
  /** Handle an exec.approval.requested event */
  handleExecApprovalRequested: (payload: ExecApprovalRequestedPayload) => void;
  /** Handle an exec.approval.resolved event */
  handleExecApprovalResolved: (payload: ExecApprovalResolvedPayload) => void;
  /** Dismiss the shutdown banner */
  dismissShutdown: () => void;
  /** Dismiss the gateway update notification */
  dismissGatewayUpdate: () => void;
  /** Reset all state (e.g., on gateway reconnect) */
  reset: () => void;
}

// ── Hook ──

export function useGatewayEvents(): UseGatewayEventsReturn {
  const [shutdown, setShutdown] = useState<ShutdownState | null>(null);
  const [presence, setPresence] = useState<PresenceEntry[]>([]);
  const [gatewayUpdateAvailable, setGatewayUpdateAvailable] = useState<GatewayEventsState['gatewayUpdateAvailable']>(null);
  const [pendingApprovals, setPendingApprovals] = useState<ExecApprovalRequest[]>([]);

  // Auto-clear shutdown after restartExpectedMs + buffer
  const shutdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up expired approvals every 30 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setPendingApprovals((prev) => {
        const filtered = prev.filter((a) => a.expiresAtMs > now);
        return filtered.length !== prev.length ? filtered : prev;
      });
    }, 30_000);
    return () => clearInterval(timer);
  }, []);

  const handleShutdown = useCallback((payload: ShutdownPayload) => {
    const state: ShutdownState = {
      active: true,
      reason: payload.reason,
      restartExpectedMs: payload.restartExpectedMs ?? null,
      receivedAt: Date.now(),
    };
    setShutdown(state);

    // Auto-dismiss shutdown banner after restart window + 30s buffer
    if (shutdownTimerRef.current) {
      clearTimeout(shutdownTimerRef.current);
    }
    const dismissAfterMs = (payload.restartExpectedMs ?? 30_000) + 30_000;
    shutdownTimerRef.current = setTimeout(() => {
      setShutdown(null);
    }, dismissAfterMs);
  }, []);

  const handlePresence = useCallback((payload: PresencePayload) => {
    setPresence(payload.presence ?? []);
  }, []);

  const handleUpdateAvailable = useCallback((payload: UpdateAvailablePayload) => {
    setGatewayUpdateAvailable(payload.updateAvailable);
  }, []);

  const handleExecApprovalRequested = useCallback((payload: ExecApprovalRequestedPayload) => {
    const request: ExecApprovalRequest = {
      id: payload.id,
      command: payload.request?.command ?? JSON.stringify(payload.request).substring(0, 100),
      createdAtMs: payload.createdAtMs,
      expiresAtMs: payload.expiresAtMs,
    };
    setPendingApprovals((prev) => {
      // Deduplicate by ID
      if (prev.some((a) => a.id === request.id)) return prev;
      return [...prev, request];
    });
  }, []);

  const handleExecApprovalResolved = useCallback((payload: ExecApprovalResolvedPayload) => {
    setPendingApprovals((prev) => prev.filter((a) => a.id !== payload.id));
  }, []);

  const dismissShutdown = useCallback(() => {
    setShutdown(null);
    if (shutdownTimerRef.current) {
      clearTimeout(shutdownTimerRef.current);
      shutdownTimerRef.current = null;
    }
  }, []);

  const dismissGatewayUpdate = useCallback(() => {
    setGatewayUpdateAvailable(null);
  }, []);

  const reset = useCallback(() => {
    setShutdown(null);
    setPresence([]);
    // Don't reset gatewayUpdateAvailable — it persists across reconnects
    setPendingApprovals([]);
    if (shutdownTimerRef.current) {
      clearTimeout(shutdownTimerRef.current);
      shutdownTimerRef.current = null;
    }
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (shutdownTimerRef.current) {
        clearTimeout(shutdownTimerRef.current);
      }
    };
  }, []);

  return {
    shutdown,
    presence,
    gatewayUpdateAvailable,
    pendingApprovals,
    handleShutdown,
    handlePresence,
    handleUpdateAvailable,
    handleExecApprovalRequested,
    handleExecApprovalResolved,
    dismissShutdown,
    dismissGatewayUpdate,
    reset,
  };
}
